import {
  createContext, useContext, useEffect, useRef, useState, useCallback,
  type ReactNode,
} from 'react'
import { useOptionalAuth } from './AuthContext'
import { updateUser } from '../lib/api'
import i18n, { applyLanguage, type LangCode } from '../i18n/index'

// Server-persisted sibling of ThemeContext for the display language. The i18n
// module already boots from per-host localStorage; this provider layers the
// cross-app sync on top: it reads user.language from /auth/me and pushes
// changes back via PATCH /users/{id}, so a language picked in one app follows
// the user into the others (localStorage alone is per-subdomain and wouldn't).
// See ThemeContext for the appearance equivalent.

interface LanguageContextValue {
  language: LangCode
  setLanguage: (next: LangCode) => Promise<void>
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function currentLang(): LangCode {
  return (i18n.language as LangCode) === 'ar' ? 'ar' : 'en'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Optional: the public Ping storefront renders this with no AuthProvider —
  // fall back to null (→ local/i18n language) instead of throwing.
  const user = useOptionalAuth()?.user ?? null
  const [language, setLanguageState] = useState<LangCode>(currentLang)

  // Sync from /auth/me ONLY the first time the user lands — mirrors
  // ThemeProvider. A NULL server value means "never chosen", so we leave the
  // local (localStorage) choice untouched rather than forcing a default; only
  // an explicit prior choice overrides the local one.
  const hasSyncedFromServer = useRef(false)
  useEffect(() => {
    if (!user || hasSyncedFromServer.current) return
    hasSyncedFromServer.current = true
    const fromServer = user.language as LangCode | null | undefined
    if (fromServer && fromServer !== currentLang()) {
      i18n.changeLanguage(fromServer)
      applyLanguage(fromServer)
      setLanguageState(fromServer)
    }
  // Only react to `user` becoming non-null; language is read fresh above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Keep local state aligned if i18n.language is changed elsewhere — e.g. the
  // pre-login LanguageToggle, which writes localStorage without going through
  // setLanguage (no user to persist to yet).
  useEffect(() => {
    const handler = (lng: string) => setLanguageState(lng === 'ar' ? 'ar' : 'en')
    i18n.on('languageChanged', handler)
    return () => { i18n.off('languageChanged', handler) }
  }, [])

  const setLanguage = useCallback(async (next: LangCode) => {
    i18n.changeLanguage(next)
    applyLanguage(next)
    setLanguageState(next)
    if (user?.id) {
      try {
        await updateUser(user.id, { language: next })
      } catch {
        // Non-blocking: the language is already applied locally; the next
        // /auth/me will reconcile if the save failed.
      }
    }
  }, [user?.id])

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider')
  return ctx
}
